import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { BetForm } from '../components/betting/BetForm';
import { Modal } from '../components/ui/Modal';
import { ParlayWidget } from '../components/parlay/ParlayWidget';
import { useEvent, useUser } from '../store';
import { deductBetAmount, calculatePayout, formatCurrency, loadEvent as loadEventFromStorage, saveEvent, mergeTemplateIntoEvent } from '../utils';
import type { Bet } from '../types';

/**
 * Bet Page
 * Page for placing a bet on a specific fight
 */
export function BetPage() {
  const { eventId, fightId } = useParams<{ eventId: string; fightId: string }>();
  const navigate = useNavigate();
  const { event, loadEvent, getFight, addBet, updateParticipant } = useEvent();
  const { currentParticipant, setCurrentParticipant } = useUser();
  
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingBet, setPendingBet] = useState<{
    fightId: string;
    fighterId: string;
    amount: number;
  } | null>(null);

  // Load event from Firestore
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    if (!eventId) {
      navigate(`/event/${eventId}`);
      return;
    }

    const loadEventData = async () => {
      setLoading(true);
      
      try {
        let eventData = await loadEventFromStorage(eventId);
        if (eventData) {
          eventData = await mergeTemplateIntoEvent(eventData);
          loadEvent(eventData);
        } else {
          navigate(`/event/${eventId}`);
        }
      } catch (err) {
        console.error('Error loading event:', err);
        navigate(`/event/${eventId}`);
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, [eventId, loadEvent, navigate]);

  // Redirect if no participant
  useEffect(() => {
    if (!loading && !currentParticipant) {
      navigate(`/join/${eventId}`);
    }
  }, [loading, currentParticipant, eventId, navigate]);

  const handleBetPlaced = (bet: { fightId: string; fighterId: string; amount: number }) => {
    setPendingBet(bet);
    setShowConfirmModal(true);
  };

  const handleConfirmBet = async () => {
    if (!pendingBet || !currentParticipant || !event) {
      return;
    }

    try {
      // CRITICAL: Reload event with latest template data to prevent using stale odds
      console.log('[BetPage] Reloading event to validate odds...');
      let freshEvent = await loadEventFromStorage(eventId!);
      if (freshEvent && freshEvent.templateId) {
        freshEvent = await mergeTemplateIntoEvent(freshEvent);
      }
      
      if (!freshEvent) {
        alert('Failed to validate bet. Please try again.');
        setShowConfirmModal(false);
        return;
      }

      const freshFight = freshEvent.fights.find((f) => f.id === pendingBet.fightId);
      if (!freshFight) {
        alert('This bet is no longer available.');
        setShowConfirmModal(false);
        return;
      }

      const fighter = freshFight.fighters.find((f) => f.id === pendingBet.fighterId);
      if (!fighter) {
        alert('Fighter not found.');
        setShowConfirmModal(false);
        return;
      }

      // Check if fight is still bettable
      if (freshFight.status !== 'upcoming') {
        alert('This fight is no longer accepting bets.');
        setShowConfirmModal(false);
        return;
      }

      // Check if sufficient funds
      if (pendingBet.amount > currentParticipant.totalAvailable) {
        alert('Insufficient funds.');
        setShowConfirmModal(false);
        return;
      }
      
      // Get the original fighter odds that were shown to user
      const originalFight = event.fights.find((f) => f.id === pendingBet.fightId);
      const originalFighter = originalFight?.fighters.find((f) => f.id === pendingBet.fighterId);
      
      // Check if odds have changed
      if (originalFighter && fighter.odds !== originalFighter.odds) {
        const confirmed = window.confirm(
          `⚠️ ODDS HAVE CHANGED!\n\n` +
          `Original: ${originalFighter.odds > 0 ? '+' : ''}${originalFighter.odds}\n` +
          `Current: ${fighter.odds > 0 ? '+' : ''}${fighter.odds}\n\n` +
          `Place bet with CURRENT odds?`
        );
        
        if (!confirmed) {
          setShowConfirmModal(false);
          // Reload page to show current odds
          window.location.reload();
          return;
        }
      }

      // Deduct bet amount from participant
      const updatedParticipant = deductBetAmount(currentParticipant, pendingBet.amount);
      if (!updatedParticipant) {
        setShowConfirmModal(false);
        return;
      }

      // Create bet with CURRENT odds (validated)
      const bet: Bet = {
        id: `bet-${Date.now()}`,
        participantId: currentParticipant.id,
        fightId: pendingBet.fightId,
        fighterId: pendingBet.fighterId,
        amount: pendingBet.amount,
        odds: fighter.odds, // Use current odds, not cached
        status: 'pending',
        placedAt: Date.now(),
      };

      console.log('[BetPage] Bet validated with current odds:', fighter.odds);

      // CRITICAL FIX: Get latest participant data to prevent bet overwrites (race condition)
      const latestParticipant = freshEvent.participants.find(p => p.id === currentParticipant.id);
      if (!latestParticipant) {
        alert('Participant not found. Please rejoin the event.');
        setShowConfirmModal(false);
        return;
      }

      // Deduct from latest participant data
      const finalUpdatedParticipant = deductBetAmount(latestParticipant, pendingBet.amount);
      if (!finalUpdatedParticipant) {
        alert('Insufficient funds (concurrent bet detected).');
        setShowConfirmModal(false);
        return;
      }

      // Update participant balance first (without the bet - addBet will handle that)
      updateParticipant(currentParticipant.id, {
        initialStakeRemaining: finalUpdatedParticipant.initialStakeRemaining,
        winningsBalance: finalUpdatedParticipant.winningsBalance,
        totalAvailable: finalUpdatedParticipant.totalAvailable,
      });

      // Add bet to event (this also updates participant's bets array)
      addBet(bet);

      // Save to Firestore using LATEST event data
      const participantWithBet = {
        ...finalUpdatedParticipant,
        bets: [...(latestParticipant.bets || []), bet],
      };
      const updatedEvent = {
        ...freshEvent,
        participants: freshEvent.participants.map((p) =>
          p.id === currentParticipant.id 
            ? participantWithBet
            : p
        ),
      };
      await saveEvent(updatedEvent);

      // Update UserContext with the new participant data (including the bet)
      setCurrentParticipant(participantWithBet);

      // Close modal and navigate back
      setShowConfirmModal(false);
      navigate(`/event/${eventId}`);
    } catch (err) {
      console.error('Error placing bet:', err);
      setShowConfirmModal(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading...</p>
        </div>
      </div>
    );
  }

  if (!event || !currentParticipant) {
    return null; // Will redirect
  }

  if (!fightId) {
    navigate(`/event/${eventId}`);
    return null;
  }

  const fight = getFight(fightId);
  if (!fight) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary-text mb-4">
              Fight Not Found
            </h1>
            <Button variant="secondary" onClick={() => navigate(`/event/${eventId}`)}>
              Back to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (fight.status !== 'upcoming') {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary-text mb-4">
              Betting Closed
            </h1>
            <p className="text-secondary-text mb-6">
              This fight is no longer accepting bets.
            </p>
            <Button variant="secondary" onClick={() => navigate(`/event/${eventId}`)}>
              Back to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const selectedFighter = pendingBet
    ? fight.fighters.find((f) => f.id === pendingBet.fighterId)
    : null;

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-primary-text">Place Bet</h1>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/event/${eventId}`)}
          >
            Cancel
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        <BetForm
          fight={fight}
          participant={currentParticipant}
          onBetPlaced={handleBetPlaced}
          onCancel={() => navigate(`/event/${eventId}`)}
        />
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Bet"
        size="md"
      >
        {pendingBet && selectedFighter && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-secondary-text">Fighter:</span>
                <span className="text-primary-text font-medium">{selectedFighter.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-secondary-text">Bet Amount:</span>
                <span className="text-primary-text font-medium">
                  {formatCurrency(pendingBet.amount)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-secondary-text">Odds:</span>
                <span className="text-primary-text font-medium">
                  {selectedFighter.odds > 0 ? '+' : ''}{selectedFighter.odds}
                </span>
              </div>
              <div className="pt-3 border-t border-border">
                <div className="flex justify-between">
                  <span className="text-secondary-text">Potential Payout:</span>
                  <span className="text-success-green font-bold text-lg">
                    {formatCurrency(calculatePayout(pendingBet.amount, selectedFighter.odds))}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                fullWidth
                onClick={handleConfirmBet}
              >
                Confirm Bet
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Parlay Widget */}
      <ParlayWidget />
    </div>
  );
}

